import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { setItems } from '../store/slices/itemsSlice';
import { api, resolveFileUrl } from '../services/api';
import useCurrency from '../hooks/useCurrency';
import useI18n from '../hooks/useI18n';
import ItemDetailView from '../components/ItemDetailView';
import PasswordLockModal from '../components/PasswordLockModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt, faFilter, faSortAmountDown,
  faGripVertical, faThLarge, faTrash, faDownload, faSearch, faTimes,
  faStar, faLock
} from '@fortawesome/free-solid-svg-icons';
import '../styles/Favorites.css';

interface FilterState {
  category: string;
  condition: string;
  priceMin: string;
  priceMax: string;
  location: string;
  tag: string;
}

const Favorites = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items } = useSelector((state: RootState) => state.items);
  const { formatPrice } = useCurrency();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'date' | 'price' | 'condition' | 'location' | 'name'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<FilterState>({
    category: '',
    condition: '',
    priceMin: '',
    priceMax: '',
    location: '',
    tag: '',
  });

  // Item detail view
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [showDetailView, setShowDetailView] = useState(false);

  useEffect(() => {
    fetchFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const response = await api.get('/items?take=100');
      const allItems = response.data.items || [];
      dispatch(setItems({ items: allItems, total: allItems.length }));
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (itemId: string) => {
    const existing = items.find(i => i.id === itemId);
    if (existing) {
      dispatch(setItems({
        items: items.map(item => item.id === itemId ? { ...item, isFavorite: !item.isFavorite } : item),
        total: items.length
      }));
    }
    try {
      const response = await api.patch(`/items/${itemId}/favorite`);
      dispatch(setItems({
        items: items.map(item => item.id === itemId ? response.data : item),
        total: items.length
      }));
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      if (existing) {
        dispatch(setItems({
          items: items.map(item => item.id === itemId ? existing : item),
          total: items.length
        }));
      }
    }
  };

  const toggleSelectItem = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) newSet.delete(itemId);
      else newSet.add(itemId);
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.id)));
    }
  };

  // Lock gate state
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockTargetItem, setLockTargetItem] = useState<any | null>(null);

  const handleViewItem = (item: any) => {
    if (item.isLocked) {
      setLockTargetItem(item);
      setShowLockModal(true);
    } else {
      setSelectedItem(item);
      setShowDetailView(true);
    }
  };

  const handleLockSuccess = () => {
    setShowLockModal(false);
    if (lockTargetItem) {
      setSelectedItem(lockTargetItem);
      setShowDetailView(true);
      setLockTargetItem(null);
    }
  };

  const handleCloseDetailView = () => {
    setShowDetailView(false);
    setSelectedItem(null);
  };

  const handleEditFromDetail = (item: any) => {
    setShowDetailView(false);
    setSelectedItem(null);
    navigate('/library', { state: { editItem: item } });
  };

  const handleDeleteFromDetail = async (itemId: string) => {
    await toggleFavorite(itemId);
    setShowDetailView(false);
    setSelectedItem(null);
  };

  const handleToggleFavoriteFromDetail = async (itemId: string) => {
    if (selectedItem && selectedItem.id === itemId) {
      setSelectedItem({ ...selectedItem, isFavorite: !selectedItem.isFavorite });
    }
    await toggleFavorite(itemId);
  };

  const removeMultiple = async () => {
    if (!window.confirm(t('favorites.removeConfirm').replace('{count}', String(selectedItems.size)))) return;
    try {
      await Promise.all(Array.from(selectedItems).map(id => api.patch(`/items/${id}/favorite`)));
      fetchFavorites();
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Failed to remove items:', error);
    }
  };

  const exportFavorites = () => {
    const data = filteredItems.map(item => ({
      name: item.name,
      category: item.category?.name,
      condition: item.condition,
      price: item.purchasePrice,
      location: item.location,
      description: item.description,
    }));
    const csv = [
      ['Name', 'Category', 'Condition', 'Price', 'Location', 'Description'],
      ...data.map(d => [d.name, d.category, d.condition, d.price, d.location, d.description])
    ].map(row => row.map(cell => `"${cell || ''}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'favorites.csv';
    a.click();
  };

  const favoriteItems = items.filter(item => item.isFavorite);

  const filteredItems = useMemo(() => {
    let result = [...favoriteItems];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item =>
        item.name.toLowerCase().includes(q) ||
        item.category?.name?.toLowerCase().includes(q) ||
        item.location?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q) ||
        ((item as any).tags || []).some((tag: string) => tag.toLowerCase().includes(q))
      );
    }
    if (filters.category) {
      result = result.filter(item => item.category?.name === filters.category);
    }
    if (filters.condition) {
      result = result.filter(item => item.condition === filters.condition);
    }
    if (filters.location) {
      result = result.filter(item => item.location === filters.location);
    }
    if (filters.tag) {
      result = result.filter(item => ((item as any).tags || []).includes(filters.tag));
    }
    if (filters.priceMin) {
      result = result.filter(item => (item.purchasePrice || 0) >= parseFloat(filters.priceMin));
    }
    if (filters.priceMax) {
      result = result.filter(item => (item.purchasePrice || 0) <= parseFloat(filters.priceMax));
    }

    result.sort((a, b) => {
      let aVal: any = '';
      let bVal: any = '';
      switch (sortBy) {
        case 'price': aVal = a.purchasePrice || 0; bVal = b.purchasePrice || 0; break;
        case 'condition': aVal = a.condition || ''; bVal = b.condition || ''; break;
        case 'location': aVal = a.location || ''; bVal = b.location || ''; break;
        case 'name': aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break;
        case 'date': default: aVal = new Date(a.createdAt).getTime(); bVal = new Date(b.createdAt).getTime();
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [favoriteItems, filters, sortBy, sortOrder, searchQuery]);

  const statistics = useMemo(() => {
    const total = filteredItems.length;
    const totalValue = filteredItems.reduce((sum, item) => sum + (item.purchasePrice || 0), 0);
    const avgValue = total > 0 ? totalValue / total : 0;
    return { total, totalValue, avgValue };
  }, [filteredItems]);

  const categories = useMemo(() =>
    Array.from(new Set(favoriteItems.map(item => item.category?.name).filter(Boolean))),
    [favoriteItems]
  );

  const conditions = useMemo(() =>
    Array.from(new Set(favoriteItems.map(item => item.condition).filter((c): c is string => !!c))),
    [favoriteItems]
  );

  const locations = useMemo(() =>
    Array.from(new Set(favoriteItems.map(item => item.location).filter(Boolean).filter((loc): loc is string => typeof loc === 'string'))),
    [favoriteItems]
  );

  const allTags = useMemo(() =>
    Array.from(new Set(favoriteItems.flatMap((item: any) => item.tags || []))),
    [favoriteItems]
  );

  if (loading) {
    return (
      <div className="fav-loading">
        <div className="fav-loading-spinner" />
        <span>{t('favorites.loading')}</span>
      </div>
    );
  }

  return (
    <div className="fav-page">
      {/* Header */}
      <div className="fav-header">
        <div className="fav-header-left">
          <h2 className="fav-title">{t('favorites.title')}</h2>
          <span className="fav-subtitle">
            {favoriteItems.length} {t('favorites.items')}{filteredItems.length !== favoriteItems.length ? ` · ${filteredItems.length} ${t('favorites.shown')}` : ''}
          </span>
        </div>
        <div className="fav-header-actions">
          <div className="fav-search-box">
            <FontAwesomeIcon icon={faSearch} className="fav-search-icon" />
            <input
              type="text"
              placeholder={t('favorites.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="fav-search-input"
            />
            {searchQuery && (
              <button className="fav-search-clear" onClick={() => setSearchQuery('')}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <button
            className={`fav-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            title={t('favorites.filters')}
          >
            <FontAwesomeIcon icon={faFilter} />
            <span>{t('favorites.filters')}</span>
          </button>
          <button
            className="fav-btn"
            onClick={() => setViewType(viewType === 'grid' ? 'list' : 'grid')}
            title={t('favorites.toggleView')}
          >
            <FontAwesomeIcon icon={viewType === 'grid' ? faGripVertical : faThLarge} />
          </button>
          {favoriteItems.length > 0 && (
            <button className="fav-btn" onClick={exportFavorites} title={t('favorites.exportCSV')}>
              <FontAwesomeIcon icon={faDownload} />
              <span>{t('favorites.export')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Compact Stats Row */}
      {filteredItems.length > 0 && (
        <div className="fav-stats-row">
          <div className="fav-stat">
            <span className="fav-stat-value">{statistics.total}</span>
            <span className="fav-stat-label">{t('favorites.items')}</span>
          </div>
          <div className="fav-stat-divider" />
          <div className="fav-stat">
            <span className="fav-stat-value">{formatPrice(statistics.totalValue, 'USD')}</span>
            <span className="fav-stat-label">{t('favorites.totalValue')}</span>
          </div>
          <div className="fav-stat-divider" />
          <div className="fav-stat">
            <span className="fav-stat-value">{formatPrice(statistics.avgValue, 'USD')}</span>
            <span className="fav-stat-label">{t('favorites.average')}</span>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="fav-filters">
          <div className="fav-filter-group">
            <label>{t('favorites.category')}</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            >
              <option value="">{t('favorites.allCategories')}</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="fav-filter-group">
            <label>{t('favorites.condition')}</label>
            <select
              value={filters.condition}
              onChange={(e) => setFilters(prev => ({ ...prev, condition: e.target.value }))}
            >
              <option value="">{t('favorites.allConditions')}</option>
              {conditions.map(cond => (
                <option key={cond} value={cond}>{t(`condition.${cond.toLowerCase().replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase())}`) || cond}</option>
              ))}
            </select>
          </div>
          <div className="fav-filter-group">
            <label>{t('favorites.minPrice')}</label>
            <input
              type="number"
              placeholder="0"
              value={filters.priceMin}
              onChange={(e) => setFilters(prev => ({ ...prev, priceMin: e.target.value }))}
            />
          </div>
          <div className="fav-filter-group">
            <label>{t('favorites.maxPrice')}</label>
            <input
              type="number"
              placeholder="999999"
              value={filters.priceMax}
              onChange={(e) => setFilters(prev => ({ ...prev, priceMax: e.target.value }))}
            />
          </div>
          <div className="fav-filter-group">
            <label>{t('favorites.location') || 'Location'}</label>
            <select
              value={filters.location}
              onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
            >
              <option value="">{t('favorites.allLocations') || 'All Locations'}</option>
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
          {allTags.length > 0 && (
            <div className="fav-filter-group">
              <label>{t('favorites.tag') || 'Tag'}</label>
              <select
                value={filters.tag}
                onChange={(e) => setFilters(prev => ({ ...prev, tag: e.target.value }))}
              >
                <option value="">{t('favorites.allTags') || 'All Tags'}</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          )}
          <button
            className="fav-filter-reset"
            onClick={() => setFilters({ category: '', condition: '', priceMin: '', priceMax: '', location: '', tag: '' })}
          >
            {t('favorites.reset')}
          </button>
        </div>
      )}

      {/* Sort & Bulk Actions Bar */}
      {filteredItems.length > 0 && (
        <div className="fav-toolbar">
          <div className="fav-sort">
            <FontAwesomeIcon icon={faSortAmountDown} className="fav-sort-icon" />
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
              <option value="date">{t('favorites.sortMostRecent')}</option>
              <option value="price">{t('favorites.sortPrice')}</option>
              <option value="name">{t('favorites.sortName')}</option>
              <option value="condition">{t('favorites.sortCondition')}</option>
              <option value="location">{t('favorites.sortLocation')}</option>
            </select>
            <button
              className="fav-sort-dir"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          <div className="fav-bulk">
            <label className="fav-select-all">
              <input
                type="checkbox"
                checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
                onChange={toggleSelectAll}
              />
              <span>{t('favorites.all')} ({filteredItems.length})</span>
            </label>
            {selectedItems.size > 0 && (
              <button className="fav-bulk-remove" onClick={removeMultiple}>
                <FontAwesomeIcon icon={faTrash} /> {t('favorites.remove')} {selectedItems.size}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Items Grid / List */}
      {filteredItems.length > 0 ? (
        <div className={`fav-${viewType}`}>
          {filteredItems.map(item => (
            <div key={item.id} className={`fav-card ${selectedItems.has(item.id) ? 'selected' : ''}`}
              onClick={() => handleViewItem(item)}
              style={{ cursor: 'pointer' }}
            >
              <label className="fav-card-check" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedItems.has(item.id)}
                  onChange={() => toggleSelectItem(item.id)}
                />
              </label>

              {item.imageUrl && (
                <div className="fav-card-img">
                  <img
                    src={resolveFileUrl(item.imageUrl) || ''}
                    alt={item.name}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}

              <div className="fav-card-top">
                <div className="fav-card-info">
                  <h4 className="fav-card-name">
                    {item.isLocked && <FontAwesomeIcon icon={faLock} style={{ marginRight: 6, fontSize: 12, opacity: 0.7 }} />}
                    {item.name}
                  </h4>
                  {item.category && <span className="fav-card-cat">{(() => { const k = `categoryName.${item.category.name}` as any; const tr = t(k); return tr !== k ? tr : item.category.name; })()}</span>}
                </div>
                <button
                  className="card-favorite-btn is-active"
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }}
                  title={t('favorites.removeFromFavorites')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="heart-icon">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </button>
              </div>

              {item.description && (
                <p className="fav-card-desc">{item.description}</p>
              )}

              <div className="fav-card-meta">
                {item.condition && (
                  <span className={`fav-cond ${item.condition.toLowerCase()}`}>
                    {t(`condition.${item.condition.toLowerCase().replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase())}`) || item.condition.replace('_', ' ')}
                  </span>
                )}
                {item.location && (
                  <span className="fav-card-loc">
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> {item.location}
                  </span>
                )}
              </div>

              {item.purchasePrice != null && item.purchasePrice > 0 && (
                <div className="fav-card-price">
                  {formatPrice(item.purchasePrice, item.purchaseCurrency || 'USD')}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="fav-empty">
          <FontAwesomeIcon icon={faStar} className="fav-empty-icon" />
          <h3>{favoriteItems.length === 0 ? t('favorites.noFavorites') : t('favorites.noMatch')}</h3>
          <p>
            {favoriteItems.length === 0
              ? t('favorites.addFromCollection')
              : t('favorites.noMatchDesc')}
          </p>
        </div>
      )}

      {/* Item Detail View Modal */}
      {selectedItem && (
        <ItemDetailView
          item={selectedItem}
          isOpen={showDetailView}
          onClose={handleCloseDetailView}
          onEdit={handleEditFromDetail}
          onDelete={handleDeleteFromDetail}
          onToggleFavorite={handleToggleFavoriteFromDetail}
        />
      )}

      {/* Lock Gate Modal */}
      {showLockModal && lockTargetItem && (
        <PasswordLockModal
          itemId={lockTargetItem.id}
          itemName={lockTargetItem.name}
          isLocked={true}
          mode="verify"
          onClose={() => { setShowLockModal(false); setLockTargetItem(null); }}
          onSuccess={handleLockSuccess}
        />
      )}
    </div>
  );
};

export default Favorites;
